/**
 * The `router.events` emitter.
 *
 * Next's pages router exposes one, and four shell components subscribe to
 * `routeChangeComplete` — AuthGuard strips a handover token after each navigation,
 * RouteTracker records the last visited route, ShellNavBridge announces the new route to
 * embedded remotes, and MarketplacePanel closes itself. React Router has no equivalent
 * event bus, so this module is it: a single module-level emitter, driven by
 * <RouterEventsBridge> on every location change.
 *
 * It is module-level rather than context-held on purpose. `router.events` is compared by
 * identity in those components' effect dependency arrays (`[router.events]`); a fresh
 * object per render would re-subscribe on every render and, worse, drop the listener the
 * previous render registered.
 */

export type RouterEvent =
  | 'routeChangeStart'
  | 'routeChangeComplete'
  | 'routeChangeError'
  | 'beforeHistoryChange'
  | 'hashChangeStart'
  | 'hashChangeComplete';

// The URL is passed to every listener and each one types its own parameter
// (`(url: string) => void`). `unknown[]` here would reject all of them — a listener
// accepting less than the emitter provides is exactly what is wanted, and that is
// contravariance TypeScript will not allow through `unknown`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Listener = (...args: any[]) => void;

const listeners = new Map<RouterEvent, Set<Listener>>();

export const routerEvents = {
  on(event: RouterEvent, cb: Listener) {
    if (!listeners.has(event)) listeners.set(event, new Set());
    listeners.get(event)!.add(cb);
  },
  off(event: RouterEvent, cb: Listener) {
    listeners.get(event)?.delete(cb);
  },
  emit(event: RouterEvent, ...args: unknown[]) {
    const set = listeners.get(event);
    if (!set) return;
    // Copy first: a listener that unsubscribes itself (AuthGuard's one-shot strip does)
    // would otherwise mutate the set mid-iteration.
    for (const cb of Array.from(set)) {
      try {
        cb(...args);
      } catch {
        // One bad subscriber must not stop the others from hearing about the navigation.
        // Swallowed rather than reported: this runs on every route change and a throwing
        // listener would otherwise flood the log with the same error per navigation.
      }
    }
  },
};
